// Lightweight monitoring bootstrap: attempts to initialize Sentry and LogRocket
// without hard dependency. Safe to include in production builds.

const MONITORING_ENABLED = String(process.env.REACT_APP_MONITORING_ENABLED || 'true').toLowerCase() !== 'false';
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';
const LOGROCKET_APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID || '';

const initSentry = async () => {
  if (!SENTRY_DSN) return;
  try {
    const Sentry = await import(/* webpackChunkName: "sentry" */ '@sentry/react');
    const Tracing = await import(/* webpackChunkName: "sentry" */ '@sentry/tracing');
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [new Tracing.BrowserTracing()],
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
      environment: process.env.NODE_ENV || 'development',
      release: process.env.REACT_APP_VERSION || undefined,
    });
    console.info('[monitoring] Sentry initialized');
    return Sentry;
  } catch (e) {
    console.warn('[monitoring] Sentry not available:', e?.message || e);
    return null;
  }
};

const initLogRocket = async () => {
  if (!LOGROCKET_APP_ID) return;
  try {
    const LogRocket = await import(/* webpackChunkName: "logrocket" */ 'logrocket');
    LogRocket.default.init(LOGROCKET_APP_ID);
    console.info('[monitoring] LogRocket initialized');
    return LogRocket.default;
  } catch (e) {
    console.warn('[monitoring] LogRocket not available:', e?.message || e);
    return null;
  }
};

export let SentryClient = null;
export let LogRocketClient = null;

export const captureError = (error, context = {}) => {
  try {
    const normalize = (e) => {
      if (!e) return new Error('unknown_error');
      if (e instanceof Error) return e;
      const name = String(e?.name || '').trim();
      const message = String(e?.message || '').trim();
      if (name || message) {
        const err = new Error(message || name || 'error');
        if (name) err.name = name;
        return err;
      }
      try {
        return new Error(typeof e === 'string' ? e : JSON.stringify(e));
      } catch (_) {
        return new Error(String(e));
      }
    };
    const err = normalize(error);
    if (SentryClient && typeof SentryClient.captureException === 'function') {
      SentryClient.captureException(err, { extra: context });
    } else {
      console.error('[monitoring] error:', err, context);
    }
  } catch (e) {
    console.error('[monitoring] captureError failed:', e);
  }
};

export const setupMonitoring = async () => {
  if (!MONITORING_ENABLED) return;
  if (typeof window === 'undefined') return;
  if (window.__kadolarMonitoringSetup) return;
  window.__kadolarMonitoringSetup = true;
  SentryClient = await initSentry();
  LogRocketClient = await initLogRocket();

  // Global handlers
  window.addEventListener('error', (evt) => {
    const err = evt?.error || (evt?.message ? new Error(evt.message) : evt);
    captureError(err, {
      source: 'window.onerror',
      message: evt?.message,
      filename: evt?.filename,
      lineno: evt?.lineno,
      colno: evt?.colno,
    });
  });
  window.addEventListener('unhandledrejection', (evt) => {
    const reason = evt?.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason || 'unhandledrejection'));
    captureError(err, { source: 'window.unhandledrejection' });
  });
};

// auto-run setup
setupMonitoring();
